import React from 'react';
import { useForm } from 'react-hook-form';

import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';

import { Button } from 'primereact/button';

import FormInputText from '../../components/form/FormInputText';
import FormPassword from '../../components/form/FormPassword';
import { IntegrationSettings } from '../../models/integrationSettings';
import { IntegrationStatus, IntegrationVendor } from '../../models/enums/integrationEnums';
import { parseIntegrationData } from '../../utils/misc-utils';
import { secretField, identifierField } from '../../utils/validation-helper';

type ArgyleVoieFormValues = {
  apiKey: string;
  apiSecret: string;
  flowId?: string;
};

interface EditArgyleVoieIntegrationSettingsProps {
  onCancel: () => void;
  onSave?: (settings: IntegrationSettings) => void;
  integrationSettings?: IntegrationSettings;
  environment: string;
}

export const EditArgyleVoieIntegrationSettings = ({
  onCancel,
  onSave,
  integrationSettings,
  environment,
}: EditArgyleVoieIntegrationSettingsProps): JSX.Element => {
  const isEditMode = !!integrationSettings;

  let apiKey = '';
  let apiSecret = '';
  let flowId = '';
  if (isEditMode && integrationSettings?.integrationData) {
    const data = parseIntegrationData(integrationSettings.integrationData);
    apiKey = data.apiKey || '';
    apiSecret = data.apiSecret || '';
    flowId = data.flowId || '';
  }

  const validationSchema = yup.object().shape({
    apiKey: secretField('API Key').required('API Key is required'),
    apiSecret: secretField('API Secret').required('API Secret is required'),
    flowId: identifierField('Flow ID'),
  });

  const { control, handleSubmit, formState } = useForm<ArgyleVoieFormValues>({
    defaultValues: {
      apiKey: apiKey,
      apiSecret: apiSecret,
      flowId: flowId,
    },
    resolver: yupResolver(validationSchema),
    mode: 'onSubmit',
  });

  const { errors } = formState;

  const onSubmit = (data: ArgyleVoieFormValues) => {

    if (onSave) {
      let settings: IntegrationSettings;

      if (isEditMode && integrationSettings) {
        settings = {
          ...integrationSettings,
          vendor: IntegrationVendor.Argyle,
          environment: environment,
          integrationData: JSON.stringify({
            apiKey: data.apiKey,
            apiSecret: data.apiSecret,
            flowId: data.flowId || undefined,
          }),
        };
      } else {
        settings = {
          name: 'Argyle VOIE Integration',
          vendor: IntegrationVendor.Argyle,
          status: IntegrationStatus.Active,
          environment: environment,
          integrationData: JSON.stringify({
            apiKey: data.apiKey,
            apiSecret: data.apiSecret,
            flowId: data.flowId || undefined,
          }),
        };
      }

      onSave(settings);
    }
  };

  return (
    <div className="surface-section p-4 border-1 surface-border border-round mb-3">
      <h3 className="mt-0 mb-3">{isEditMode ? 'Edit Argyle VOIE Settings' : 'Argyle VOIE Settings'}</h3>
      <form onSubmit={handleSubmit(onSubmit)} noValidate className="pb-3 p-fluid p-input-filled">
        <div className="field mb-4">
          <FormInputText
            control={control}
            errors={errors}
            field="apiKey"
            label="API Key *"
            placeholder="Enter your Argyle API Key"
          />
        </div>

        <div className="field mb-4">
          <FormPassword
            control={control}
            errors={errors}
            field="apiSecret"
            label="API Secret *"
            placeholder="Enter your Argyle API Secret"
            feedback={false}
            toggleMask
          />
        </div>

        <div className="field mb-4">
          <FormInputText
            control={control}
            errors={errors}
            field="flowId"
            label="Flow ID"
            placeholder="Enter your Argyle Flow ID (optional)"
          />
        </div>

        <div className="flex justify-content-end gap-2 pt-4">
          <Button
            type="button"
            label="Cancel"
            className="p-button-outlined"
            style={{ width: 'auto', minWidth: 'auto' }}
            onClick={onCancel}
          />
          <Button
            type="submit"
            label={isEditMode ? 'Update' : 'Save'}
            className="p-button"
            style={{ width: 'auto', minWidth: 'auto' }}
          />
        </div>
      </form>
    </div>
  );
};
